import React, { useState } from "react";
import { Input, Card, Row, Col, Button, Select, Modal, List, Spin } from "antd";
import { GoogleOutlined, QuestionOutlined } from "@ant-design/icons";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";

const clientId = "AIzaSyAqvza_oHM3LIMzO72DsJ1tfx4mmDlZA1E"; // replace with env variable

const integrations = [
  {
    title: "Google Reviews",
    description:
      "Integrate with Google to pull reviews from your business account. Enable to fetch real-time feedback and insights directly into your dashboard.",
    icon: <GoogleOutlined style={{ fontSize: 28, color: "#4285F4" }} />,
    heading: "Google Integration",
  },
  {
    title: "TripAdvisor",
    description: "Coming soon: Integrate with TripAdvisor for hospitality reviews.",
    icon: <QuestionOutlined style={{ fontSize: 28, color: "#999" }} />,
    dotted: true,
  },
];

const IntegrationLanding = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);

  const handleSearch = (value) => setSearchQuery(value.toLowerCase());
  const filteredIntegrations = integrations.filter((i) =>
    i.title.toLowerCase().includes(searchQuery)
  );

  // Google login hook
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setAccessToken(tokenResponse.access_token);
      setIsConnected(true);
      setLoading(true);
      try {
        // fetch accounts
        const res = await fetch(
          "https://mybusinessaccountmanagement.googleapis.com/v1/accounts",
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        );
        const data = await res.json();
        setAccounts(data.accounts || []);
        setShowSelectionModal(true);
      } catch (err) {
        console.error("Error fetching accounts:", err);
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google Connect Error:", error);
      setIsConnected(false);
    },
    scope: "https://www.googleapis.com/auth/business.manage",
  });

  // fetch locations for selected account
  const handleAccountSelect = async (accountName) => {
    setSelectedAccount(accountName);
    setLoading(true);
    try {
      const res = await fetch(
        `https://mybusinessbusinessinformation.googleapis.com/v1/${accountName}/locations`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const data = await res.json();
      setLocations(data.locations || []);
    } catch (err) {
      console.error("Error fetching locations:", err);
    } finally {
      setLoading(false);
    }
  };

  // fetch reviews for selected location
  const handleLocationSelect = async (loc) => {
    setSelectedLocation(loc);
    setLoading(true);
    try {
      const res = await fetch(
        `https://mybusiness.googleapis.com/v4/${loc.name}/reviews`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      const data = await res.json();
      setReviews(data.reviews || []);
      // optionally save to backend here:
      // await axios.post('/api/integrations/google/reviews', { location: loc, reviews: data.reviews })
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="page-wrapper-full">
        <div className="content container-fluid">
          <div className="page-header d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 style={{ fontSize: "24px", fontWeight: 600 }}>Feedback Integrations</h3>
              <p style={{ fontSize: "14px", color: "#666" }}>
                Connect external platforms to import reviews and enrich your feedback analysis.
              </p>
            </div>
            <Input.Search
              placeholder="Search integrations..."
              allowClear
              enterButton
              onSearch={handleSearch}
              style={{ width: 300 }}
            />
          </div>

          <Row gutter={[16, 16]}>
            {filteredIntegrations.map((integration, idx) => (
              <Col xs={24} sm={12} md={8} key={idx}>
                <Card
                  hoverable
                  style={{
                    borderRadius: 8,
                    ...(integration.dotted ? { border: "2px dotted #999" } : {}),
                  }}
                >
                  {integration.heading && (
                    <h5 style={{ marginBottom: 4, fontWeight: 600 }}>
                      {integration.heading}
                    </h5>
                  )}
                  <div className="d-flex align-items-center gap-3">
                    {integration.icon}
                    <div>
                      <h4 style={{ marginBottom: 4 }}>{integration.title}</h4>
                      <p style={{ fontSize: "13px", color: "#666" }}>
                        {integration.description}
                      </p>
                    </div>
                  </div>
                  {!integration.dotted && (
                    <Button
                      type={isConnected ? "default" : "primary"}
                      onClick={() => (isConnected ? setIsConnected(false) : login())}
                      style={{ marginTop: 12 }}
                    >
                      {isConnected ? "Disconnect" : "Connect with Google"}
                    </Button>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      <Modal
        title="Select Business Account & Location"
        open={showSelectionModal}
        onCancel={() => setShowSelectionModal(false)}
        footer={null}
        width={650}
      >
        <Spin spinning={loading}>
          <div>
            <h6>Select Account:</h6>
            <Select
              style={{ width: "100%", marginBottom: 16 }}
              placeholder="Select an account"
              onChange={handleAccountSelect}
              value={selectedAccount}
            >
              {accounts.map((acc) => (
                <Select.Option key={acc.name} value={acc.name}>
                  {acc.accountName || acc.name}
                </Select.Option>
              ))}
            </Select>

            {locations.length > 0 && (
              <>
                <h6>Select Location:</h6>
                <List
                  bordered
                  dataSource={locations}
                  renderItem={(loc) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => handleLocationSelect(loc)}>
                          Select
                        </Button>,
                      ]}
                    >
                      {loc.title || loc.name}
                    </List.Item>
                  )}
                />
              </>
            )}

            {reviews.length > 0 && (
              <>
                <h6 className="mt-3">Latest Reviews:</h6>
                <List
                  dataSource={reviews}
                  renderItem={(rev) => (
                    <List.Item>
                      <div>
                        <strong>{rev.reviewer?.displayName}</strong> – {rev.starRating}⭐
                        <p>{rev.comment || "No comment"}</p>
                      </div>
                    </List.Item>
                  )}
                />
              </>
            )}
          </div>
        </Spin>
      </Modal>
    </GoogleOAuthProvider>
  );
};

export default IntegrationLanding;
